/**
 * Configurazione e utility per la navigazione cross-area
 * Gestisce la navigazione tra area medica, contabile e amministrativa
 */

// Definizione delle aree
export const AREAS = {
    CENTRO_MEDICO: 'centro_medico',
    CENTRO_CONTABILE: 'centro_contabile',
    CENTRO_AMMINISTRATIVO: 'centro_amministrativo'
};

// Configurazione delle aree con le loro rotte principali
export const AREA_CONFIG = {
    [AREAS.CENTRO_MEDICO]: {
        label: 'Area Medica',
        icon: 'UserOutlined',
        basePaths: [
            '/dashboards/scadenze',
            '/dashboards/calendario',
            '/pazienti',
            '/scheda-paziente'
        ]
    },
    [AREAS.CENTRO_CONTABILE]: {
        label: 'Area Contabile',
        icon: 'BarChartOutlined',
        basePaths: [
            '/finance',
            '/magazzino',
            '/scheda-ddt',
            '/scheda-lotto',
            '/scheda-articolo',
            '/scheda-fornitore',
            '/scheda-magazzino'
        ]
    },
    [AREAS.CENTRO_AMMINISTRATIVO]: {
        label: 'Area Amministrativa',
        icon: 'SettingOutlined',
        basePaths: [
            '/ruoli',
            '/gestisci-utenti'
        ]
    }
};

// Ruoli che possono accedere a ciascuna area
export const AREA_ROLES = {
    [AREAS.CENTRO_MEDICO]: ['superadmin', 'operatore', 'operatore_contabile'],
    [AREAS.CENTRO_CONTABILE]: ['superadmin', 'operatore_contabile', 'contabile'],
    [AREAS.CENTRO_AMMINISTRATIVO]: ['superadmin']
};

/**
 * Determina a quale area appartiene un path
 * @param {string} path - Il path da verificare
 * @returns {string|null} - L'area di appartenenza o null se non trovata
 */
export const getAreaFromPath = (path) => {
    for (const [area, config] of Object.entries(AREA_CONFIG)) {
        for (const basePath of config.basePaths) {
            if (path.includes(basePath)) {
                return area;
            }
        }
    }
    return null;
};

/**
 * Verifica se un utente può accedere a un'area specifica
 * @param {string} roleName - Nome del ruolo dell'utente
 * @param {string} targetArea - Area di destinazione
 * @returns {boolean} - true se l'utente può accedere
 */
export const canAccessArea = (roleName, targetArea) => {
    const allowedRoles = AREA_ROLES[targetArea] || [];
    return allowedRoles.includes(roleName);
};

/**
 * Verifica se un utente può navigare verso un path specifico
 * @param {string} roleName - Nome del ruolo dell'utente
 * @param {string} path - Path di destinazione
 * @returns {{canNavigate: boolean, targetArea: string|null, needsAreaSwitch: boolean, currentArea: string}}
 */
export const checkNavigationPermission = (roleName, currentArea, targetPath) => {
    const targetArea = getAreaFromPath(targetPath);

    // Se non riusciamo a determinare l'area, permettiamo la navigazione
    if (!targetArea) {
        return {
            canNavigate: true,
            targetArea: null,
            needsAreaSwitch: false,
            currentArea
        };
    }

    // Se siamo già nell'area corretta, non serve cambio
    if (targetArea === currentArea) {
        return {
            canNavigate: true,
            targetArea,
            needsAreaSwitch: false,
            currentArea
        };
    }

    // Verifica se l'utente può accedere all'area di destinazione
    const hasPermission = canAccessArea(roleName, targetArea);

    return {
        canNavigate: hasPermission,
        targetArea,
        needsAreaSwitch: hasPermission,
        currentArea
    };
};

/**
 * Ottiene l'etichetta dell'area
 * @param {string} area - Codice dell'area
 * @returns {string} - Etichetta localizzata
 */
export const getAreaLabel = (area) => {
    return AREA_CONFIG[area]?.label || area;
};
